import type { CharacterSpec } from "../render/CharacterModel";
import { Combatant, type Stats } from "./Stats";
import type { ItemDef } from "./Skills";

export interface LearnEntry {
  level: number;
  skill: string;
}

export interface HeroClassDef {
  id: string;
  name: string;
  appearance: CharacterSpec;
  /** Stats at level 1. */
  base: Stats;
  /** Added per level after the first. Fractions accumulate, then round. */
  growth: Partial<Stats>;
  learnset?: LearnEntry[];
  maxLevel?: number;
}

export interface LevelUp {
  level: number;
  gains: Partial<Stats>;
  learned: string[];
}

/** Total experience required to *reach* a level. */
export function totalExpForLevel(level: number): number {
  if (level <= 1) return 0;
  const n = level - 1;
  return Math.round(6 * Math.pow(n, 2.05) + 4 * n);
}

export interface Equipment {
  weapon?: ItemDef;
  armor?: ItemDef;
  accessory?: ItemDef;
}

export class Hero extends Combatant {
  readonly def: HeroClassDef;
  readonly name: string;
  level: number;
  exp = 0;
  skills: string[] = [];
  equipment: Equipment = {};

  constructor(name: string, def: HeroClassDef, level = 1) {
    super();
    this.name = name;
    this.def = def;
    this.level = level;
    this.exp = totalExpForLevel(level);
    this.skills = (def.learnset ?? []).filter((e) => e.level <= level).map((e) => e.skill);
    this.fullHeal();
  }

  get appearance(): CharacterSpec {
    return this.def.appearance;
  }

  get maxLevel(): number {
    return this.def.maxLevel ?? 30;
  }

  get stats(): Stats {
    const base = this.statsAtLevel(this.level);
    const eq = [this.equipment.weapon, this.equipment.armor, this.equipment.accessory];
    for (const item of eq) {
      if (!item) continue;
      base.attack += item.attack ?? 0;
      base.defense += item.defense ?? 0;
      base.agility += item.agility ?? 0;
      base.maxHp += item.maxHp ?? 0;
    }
    return base;
  }

  statsAtLevel(level: number): Stats {
    const b = this.def.base;
    const g = this.def.growth;
    const n = level - 1;
    return {
      maxHp: Math.round(b.maxHp + (g.maxHp ?? 0) * n),
      maxMp: Math.round(b.maxMp + (g.maxMp ?? 0) * n),
      attack: Math.round(b.attack + (g.attack ?? 0) * n),
      defense: Math.round(b.defense + (g.defense ?? 0) * n),
      agility: Math.round(b.agility + (g.agility ?? 0) * n),
      evasion: b.evasion ?? 0,
    };
  }

  get expToNext(): number {
    if (this.level >= this.maxLevel) return 0;
    return Math.max(0, totalExpForLevel(this.level + 1) - this.exp);
  }

  /**
   * Awards experience and applies every level gained. Returns one entry per
   * level so the caller can narrate them in order.
   */
  gainExp(amount: number): LevelUp[] {
    if (!this.alive) return [];
    this.exp += amount;
    const results: LevelUp[] = [];

    while (this.level < this.maxLevel && this.exp >= totalExpForLevel(this.level + 1)) {
      const before = this.statsAtLevel(this.level);
      this.level++;
      const after = this.statsAtLevel(this.level);

      const gains: Partial<Stats> = {};
      for (const key of ["maxHp", "maxMp", "attack", "defense", "agility"] as const) {
        const delta = after[key] - before[key];
        if (delta !== 0) gains[key] = delta;
      }

      // Raising max HP/MP hands the difference to current HP/MP, which is the
      // small mercy every game in this genre gives you.
      this.hp += gains.maxHp ?? 0;
      this.mp += gains.maxMp ?? 0;

      const learned: string[] = [];
      for (const entry of this.def.learnset ?? []) {
        if (entry.level === this.level && !this.skills.includes(entry.skill)) {
          this.skills.push(entry.skill);
          learned.push(entry.skill);
        }
      }

      results.push({ level: this.level, gains, learned });
    }

    return results;
  }
}
